/**
 * Authentication Context for BNOVA Business Dashboard
 * Manages user authentication state and provides login/logout functionality
 */

import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

import API_BASE_URL from "../config/api";

const AuthContext = createContext();

// API base URL
const API_URL = API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const storedUser = localStorage.getItem("admin_user");

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (!parsedUser.roleType) parsedUser.roleType = "default";
        setUser(parsedUser);
      } catch (err) {
        console.error("Failed to parse user data:", err);
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      }
    }
    setLoading(false);
  }, []);

  // Login function
  // roleType: 'default' | 'super'. When 'super', superAccessKey must match backend SUPER_ACCESS_KEY
  const login = async (email, password, roleType = "default", superAccessKey = "") => {
    setError(null);
    setLoading(true);

    try {
      const body = { email, password, roleType: roleType === "super" ? "super" : "default" };
      if (roleType === "super") body.superAccessKey = superAccessKey;

      const response = await fetch(`${API_URL}/auth/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Login failed");
      }

      // Store token and user data (user includes roleType from backend)
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.user));

      setUser(data.user);
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  // Register function
  const register = async (name, email, password) => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/admin/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Registration failed");
      }

      // Auto-login after registration
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.user));

      setUser(data.user);
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    setUser(null);
    setError(null);
  };

  // Get auth token
  const getToken = () => {
    return localStorage.getItem("admin_token");
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    getToken,
    isAuthenticated: !!user,
    isSuperAccess: user?.roleType === "super",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
